"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTurnstile } from "@/hooks/useTurnstile";

const DELAY_MS = 5000;
const EXIT_MS = 250;
const DEFAULT_HEADLINE = "Get access to the full report.";

interface ReportRequestPopupProps {
  collection: "co-analyst" | "posts";
  slug: string;
  title: string;
  /** Keystatic `reportPopup.headline`; blank → DEFAULT_HEADLINE. */
  headline?: string | null;
  /** Keystatic `reportPopup.remember`: hide it on this browser (localStorage)
   *  once closed or submitted. Off → nothing is stored, shows every visit. */
  remember?: boolean;
}

/**
 * "Email me the report" popup, switched on per article by the Keystatic
 * `reportPopup.enabled` flag (both research and blog). Opens 5s after the
 * reader lands; POSTs to `/api/leads/report`, which saves the lead in Copper
 * and emails the team, who send the report by hand.
 *
 * With `remember` on, closing or submitting hides it on this browser for good
 * (one localStorage key per article); with it off, nothing is stored and it
 * opens on every visit. If another modal (the trial chooser) is open when the
 * timer fires, it waits for that to close.
 *
 * Visually it's the trial modal's sibling: same dark card, display-face
 * headline, "$600B+ AUM" trust line.
 */
export default function ReportRequestPopup({ collection, slug, title, headline, remember = false }: ReportRequestPopupProps) {
  const storageKey = `report-popup:${collection}/${slug}`;
  const rememberClosed = () => {
    if (!remember) return;
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // Blocked storage — it just shows again next visit.
    }
  };

  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { containerRef, token, reset } = useTurnstile({ theme: "dark", active: visible });
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (remember) {
      try {
        if (localStorage.getItem(storageKey) === "1") return;
      } catch {
        // Blocked storage — show the popup anyway.
      }
    }
    let poll: ReturnType<typeof setInterval> | undefined;
    const open = () => {
      setVisible(true);
      requestAnimationFrame(() => setShown(true));
    };
    const timer = setTimeout(() => {
      if (!document.querySelector('[aria-modal="true"]')) return open();
      poll = setInterval(() => {
        if (document.querySelector('[aria-modal="true"]')) return;
        clearInterval(poll);
        open();
      }, 1000);
    }, DELAY_MS);
    return () => {
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [remember, storageKey]);

  // While open: ESC closes, the page behind doesn't scroll, and focus moves
  // in — to the input with a mouse, to the panel on touch (so the keyboard
  // doesn't jump up uninvited).
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus();
    else panelRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
    };
    // close() only touches state setters and props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const close = () => {
    setShown(false);
    rememberClosed();
    setTimeout(() => setVisible(false), EXIT_MS);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!token) {
      setError("Please complete the verification.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/leads/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, collection, slug, turnstileToken: token }),
      });
      const data = await res.json();
      if (!res.ok) {
        const details = data.details as Record<string, string[]> | undefined;
        setError(details?.email?.[0] ? "Please enter a valid email address." : data.error || "Something went wrong.");
        reset();
        return;
      }
      setSuccess(true);
      rememberClosed();
    } catch {
      setError("Network error. Please try again.");
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[90] flex items-center justify-center p-4 transition-opacity duration-200 ${
        shown ? "opacity-100" : "opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-popup-title"
    >
      <div
        className="absolute inset-0 backdrop-blur-[2px]"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundColor: "rgba(10,10,9,0.72)",
          backgroundSize: "22px 22px",
        }}
        onClick={close}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative w-full max-w-[420px] max-h-[85dvh] overflow-y-auto rounded-2xl border border-white/10 bg-[#141413] px-[26px] pt-7 pb-[22px] outline-none shadow-[0_24px_80px_-16px_rgba(0,0,0,0.7)] transition-transform duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          shown ? "translate-y-0 scale-100" : "translate-y-3 scale-[0.98]"
        }`}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-[18px] right-[18px] flex h-8 w-8 items-center justify-center rounded-lg text-[#6B6B6A] hover:text-white hover:bg-white/[0.06] transition-colors"
          aria-label="Close dialog"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 id="report-popup-title" className="font-[family-name:var(--font-display)] text-xl font-semibold text-white mb-2">
              Request received
            </h2>
            <p className="text-[13px] leading-relaxed text-[#A0A09F]">
              Our team will send the report to <span className="text-white">{email}</span> shortly.
            </p>
            <button onClick={close} className="mt-6 text-sm font-medium text-[#B98AE0] hover:underline transition-colors">
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-white/50 mb-2">Free report</p>
            <h2
              id="report-popup-title"
              className="font-[family-name:var(--font-display)] text-[26px] font-semibold tracking-[-0.015em] leading-[1.12] text-white mb-2 pr-7"
            >
              {headline?.trim() || DEFAULT_HEADLINE}
            </h2>
            <p className="text-[13.5px] leading-relaxed text-[#A0A09F] mb-5">
              Enter your email and we&apos;ll send you <span className="text-white">{title}</span>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                ref={inputRef}
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="name@company.com"
                aria-label="Work email"
                disabled={submitting}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-[#2A2A28] bg-[#1C1C1A] px-4 py-3 text-base sm:text-sm text-white placeholder:text-[#6B6B6A] focus:outline-none focus:ring-2 focus:ring-[#B98AE0]/30 focus:border-[#3A3A37] transition-colors disabled:opacity-50"
              />

              <div ref={containerRef} />

              {error && (
                <p className="text-sm text-red-300" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-3 text-sm font-medium text-white transition-colors"
              >
                {submitting ? "Sending…" : "Get the report"}
                {!submitting && (
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            </form>
          </>
        )}

        <hr className="my-4 border-0 border-t border-white/10" />

        <p className="text-[11px] text-white/50 text-center">
          Trusted by funds managing <span className="font-semibold text-white">$600B+</span> in AUM
        </p>
      </div>
    </div>
  );
}
